import { companyApi, CompanyVo } from '@/axios/company';
import { useRequest, useSetState } from 'ahooks';
import { ColumnsType } from 'antd/es/table';
import { Button, Typography, InputNumber, Space, Flex, DatePicker, Select } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import styles from './index.module.less';
import SearchInput from '@/components/SearchInput';
import { openEditCompanyModal } from './components/Edit';
import { useNavigate } from 'react-router-dom';
import CompanyDetail from './components/Detail';
import AccessButton from '@/components/AccessButton';
import OperatorBtns from '@/components/Table/operateBtns';
import SVGIcon from '@/components/SVGIcon';
import AddIcon from '@/assets/icons/add.svg?react';
import CommonTable from '@/components/Table';
import CreateButton from '@/components/Base/CreateButton';
import EllipsisText from '@/components/Table/EllipsisText';
import { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
const { Text } = Typography;
const { RangePicker } = DatePicker;

const COMPANY_STATUS_OPTIONS = [
  { text: '首次交付 - 交付中', value: 'DELIVERING' },
  { text: '首次交付 - 待验收', value: 'FIRST_WAIT_ACCEPTANCE' },
  { text: '首次交付 - 已超期', value: 'FIRST_OVERDUE' },
  { text: '首次交付 - 已上线', value: 'ONLINE' },
  { text: '首次交付 - 已上线+继续优化', value: 'FIRST_ONLINE_OPTIMIZING' },
  { text: '补词优化 - 交付中', value: 'SUPPLEMENT_DELIVERING' },
  { text: '补词优化 - 待验收', value: 'SUPPLEMENT_WAIT_ACCEPTANCE' },
  { text: '补词优化 - 未达标', value: 'SUPPLEMENT_NOT_STANDARD' },
  { text: '补词优化 - 已优化', value: 'SUPPLEMENT_OPTIMIZED' },
  { text: '测试', value: 'TESTING' },
  { text: '停止交付', value: 'STOPPED' },
] as const;

type CompanyStatus = (typeof COMPANY_STATUS_OPTIONS)[number]['value'];

const isCompanyStatus = (value: string): value is CompanyStatus =>
  COMPANY_STATUS_OPTIONS.some((item) => item.value === value);

const toSelectOptions = (values?: string[]) => (values || []).map((value) => ({ label: value, value }));

const OptionFilterDropdown = ({
  value,
  options,
  placeholder,
  onConfirm,
  confirm,
  clearFilters,
}: {
  value: string[];
  options: { label: string; value: string }[];
  placeholder: string;
  onConfirm: (value: string[]) => void;
} & Pick<FilterDropdownProps, 'confirm' | 'clearFilters'>) => {
  const [tempValue, setTempValue] = useState<string[]>(value);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  return (
    <div style={{ padding: 8, width: 260 }}>
      <Flex vertical gap={8}>
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          maxTagCount="responsive"
          placeholder={placeholder}
          value={tempValue}
          options={options}
          onChange={setTempValue}
          style={{ width: '100%' }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => {
              onConfirm(tempValue);
              confirm();
            }}
          >
            确定
          </Button>
          <Button
            size="small"
            onClick={() => {
              setTempValue([]);
              onConfirm([]);
              clearFilters?.();
            }}
          >
            重置
          </Button>
        </Space>
      </Flex>
    </div>
  );
};

const Company = () => {
  const [params, setParams] = useSetState<any>({
    pageIndex: 1,
    pageSize: 10,
    companyName: '',
    orderItems: [],
    statuses: [],
    combos: [],
    providers: [],
    industries: [],
    customerTypes: [],
    analysisExposureMin: 0,
    analysisExposureMax: 100,
    expireTimeStart: '',
    expireTimeEnd: '',
  });
  const [tempExposureMin, setTempExposureMin] = useState<number>(0);
  const [tempExposureMax, setTempExposureMax] = useState<number>(100);
  const [tempExpireRange, setTempExpireRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const { data, loading, runAsync: refreshList } = useRequest(
    () => {
      return companyApi.list({
        ...params,
        statuses: params.statuses.length ? params.statuses : undefined,
        combos: params.combos.length ? params.combos : undefined,
        providers: params.providers.length ? params.providers : undefined,
        industries: params.industries.length ? params.industries : undefined,
        customerTypes: params.customerTypes.length ? params.customerTypes : undefined,
        expireTimeStart: params.expireTimeStart || undefined,
        expireTimeEnd: params.expireTimeEnd || undefined,
      });
    },
    {
      refreshDeps: [params],
    }
  );
  const { data: fieldOptions } = useRequest(() => companyApi.fieldOptions());
  const comboOptions = toSelectOptions(fieldOptions?.combos);
  const providerOptions = toSelectOptions(fieldOptions?.providers);
  const industryOptions = toSelectOptions(fieldOptions?.industries);
  const customerTypeOptions = toSelectOptions(fieldOptions?.customerTypes);
  const navigate = useNavigate();
  const columns: ColumnsType<CompanyVo> = [
    {
      title: '企业名称',
      dataIndex: 'companyName',
      fixed: 'left',
      width: 200,
      ellipsis: true,
      render: (value: any, record: CompanyVo) => (
        <a
          onClick={() => {
            navigate(`/company?companyId=${record.companyId}`);
          }}
        >
          <EllipsisText text={value} />
        </a>
      ),
    },
    {
      title: '曝光度',
      dataIndex: 'analysisExposure',
      width: 150,
      filtered: params.analysisExposureMin !== 0 || params.analysisExposureMax !== 100,
      filterDropdown: ({ confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Flex vertical gap={8} style={{ width: '100%' }}>
            <Space>
              <InputNumber
                placeholder="最低曝光度"
                min={0}
                max={100}
                value={tempExposureMin}
                onChange={(value) => setTempExposureMin(value || 0)}
                style={{ width: 120 }}
              />
              <span>-</span>
              <InputNumber
                placeholder="最高曝光度"
                min={0}
                max={100}
                value={tempExposureMax}
                onChange={(value) => setTempExposureMax(value || 0)}
                style={{ width: 120 }}
              />
            </Space>
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  setParams({
                    pageIndex: 1,
                    analysisExposureMin: tempExposureMin,
                    analysisExposureMax: tempExposureMax,
                  });
                  confirm();
                }}
              >
                确定
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setTempExposureMin(0);
                  setTempExposureMax(100);
                  setParams({
                    pageIndex: 1,
                    analysisExposureMin: 0,
                    analysisExposureMax: 100,
                  });
                  clearFilters?.();
                }}
              >
                重置
              </Button>
            </Space>
          </Flex>
        </div>
      ),
      render: (value) => (value || 0) + '%',
      sorter: true,
      // sortDirections: ['ascend', 'descend', 'ascend'],
      // defaultSortOrder: 'ascend',
    },
    {
      title: '最近一次任务时间',
      dataIndex: 'latestTaskTime',
      width: 200,
      sorter: true,
      render: (val) => (val ? new Date(val).toLocaleString() : '-'),
    },
    {
      title: '最近一次任务名称',
      dataIndex: 'latestTaskName',
      width: 200,
      ellipsis: true,
      render: (val) =>
        val ? (
          <Text
            ellipsis={{
              tooltip: val,
            }}
          >
            {val}
          </Text>
        ) : (
          '-'
        ),
    },
    {
      title: '客户类型',
      dataIndex: 'customerType',
      width: 140,
      filtered: params.customerTypes.length > 0,
      filterDropdown: ({ confirm, clearFilters }) => (
        <OptionFilterDropdown
          value={params.customerTypes}
          options={customerTypeOptions}
          placeholder="筛选客户类型"
          confirm={confirm}
          clearFilters={clearFilters}
          onConfirm={(value) => setParams({ pageIndex: 1, customerTypes: value })}
        />
      ),
      render: (value) => value || '-',
    },
    {
      title: '套餐类型',
      dataIndex: 'combo',
      filtered: params.combos.length > 0,
      filterDropdown: ({ confirm, clearFilters }) => (
        <OptionFilterDropdown
          value={params.combos}
          options={comboOptions}
          placeholder="筛选套餐类型"
          confirm={confirm}
          clearFilters={clearFilters}
          onConfirm={(value) => setParams({ pageIndex: 1, combos: value })}
        />
      ),
    },
    {
      title: '行业',
      dataIndex: 'industry',
      width: 140,
      filtered: params.industries.length > 0,
      filterDropdown: ({ confirm, clearFilters }) => (
        <OptionFilterDropdown
          value={params.industries}
          options={industryOptions}
          placeholder="筛选行业"
          confirm={confirm}
          clearFilters={clearFilters}
          onConfirm={(value) => setParams({ pageIndex: 1, industries: value })}
        />
      ),
      render: (value) => value || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      filters: COMPANY_STATUS_OPTIONS.map((item) => ({ ...item })),
      filterMultiple: true,
      filteredValue: params.statuses.length ? params.statuses : null,
      width: 190,
      render: (_value, record) => record.statusDesc,
    },
    {
      title: '到期时间',
      dataIndex: 'expireTime',
      width: 180,
      filtered: !!params.expireTimeStart || !!params.expireTimeEnd,
      filterDropdown: ({ confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Flex vertical gap={8}>
            <RangePicker
              allowEmpty={[true, true]}
              value={tempExpireRange}
              onChange={(value) => {
                setTempExpireRange((value as [Dayjs | null, Dayjs | null] | null) ?? null);
              }}
            />
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  setParams({
                    pageIndex: 1,
                    expireTimeStart: tempExpireRange?.[0] ? tempExpireRange[0].format('YYYY-MM-DD') : '',
                    expireTimeEnd: tempExpireRange?.[1] ? tempExpireRange[1].format('YYYY-MM-DD') : '',
                  });
                  confirm();
                }}
              >
                确定
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setTempExpireRange(null);
                  setParams({
                    pageIndex: 1,
                    expireTimeStart: '',
                    expireTimeEnd: '',
                  });
                  clearFilters?.();
                }}
              >
                重置
              </Button>
            </Space>
          </Flex>
        </div>
      ),
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD') : '-'),
    },
    {
      title: '服务商',
      dataIndex: 'provider',
      filtered: params.providers.length > 0,
      filterDropdown: ({ confirm, clearFilters }) => (
        <OptionFilterDropdown
          value={params.providers}
          options={providerOptions}
          placeholder="筛选服务商"
          confirm={confirm}
          clearFilters={clearFilters}
          onConfirm={(value) => setParams({ pageIndex: 1, providers: value })}
        />
      ),
    },
    {
      title: '备注',
      width: 200,
      dataIndex: 'remark',
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 200,
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      fixed: 'right',
      // width: 120,
      render: (_, record) => {
        return (
          <OperatorBtns
            btns={[
              {
                type: 'common',
                text: '查看',
                onClick: () => {
                  navigate(`/company?companyId=${record.companyId}`);
                },
              },
              {
                type: 'edit',
                permission: ['company:edit'],
                onClick: () => {
                  openEditCompanyModal({ id: record.companyId }, { title: '编辑企业' }, refreshList);
                },
              },
            ]}
          />
        );
      },
    },
  ];
  const companyId = new URLSearchParams(window.location.search).get('companyId');
  if (companyId) {
    return (
      <CompanyDetail
        onReturn={() => {
          navigate('/company');
        }}
      />
    );
  }
  return (
    <div className={styles.company}>
      <div className={styles.toolbar}>
        <AccessButton permission={['company:create']}>
          <CreateButton
            text="新增企业"
            onClick={() => {
              openEditCompanyModal(undefined, { title: '创建企业' }, () => {
                setParams({ pageIndex: 1 });
              });
            }}
            icon={<SVGIcon icon={<AddIcon />} />}
          />
        </AccessButton>
        <div></div>
        <SearchInput
          onSearch={(val) => {
            setParams({ pageIndex: 1, companyName: val });
          }}
          style={{ width: '200px' }}
          placeholder="请输入企业名称"
        />
      </div>
      <div className={styles.tableContainer}>
        <CommonTable<CompanyVo>
          loading={loading}
          rowKey={'companyId'}
          columns={columns}
          dataSource={data?.records || []}
          total={data?.total}
          params={params}
          setParams={setParams}
          onChange={(pagination, filters, sorter) => {
            console.log('filters', filters, sorter, pagination);
            const statusFilters = (((filters?.status as React.Key[] | null) || [])
              .map((item) => String(item))
              .filter((item): item is CompanyStatus => isCompanyStatus(item)));
            setParams({
              pageIndex: pagination.current!,
              pageSize: pagination.pageSize || params.pageSize,
              statuses: statusFilters,
              orderItems: (sorter as any)?.order
                ? [
                    {
                      field: (sorter as any)?.field || 'latestExposure',
                      type: (sorter as any)?.order === 'ascend' ? 'ASC' : 'DESC',
                    },
                  ]
                : [],
            });
          }}
        />
      </div>
    </div>
  );
};

export default Company;
